import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  CalendarClock,
  Clock,
  Info,
  Loader2,
  MapPin,
  Package,
  Printer,
  RefreshCw,
  Truck,
  X,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  isAdminRescheduleStructuredError,
  useCancelPickupBookingAdmin,
  useDetachShipmentFromPickupBookingAdmin,
  useGetPickupBookings,
  usePrintPickupBookingAdmin,
  useReschedulePickupBookingAdmin,
  useUpdatePickupBookingNotesAdmin,
  type AdminPickupBooking,
  type PickupBookingDerivedState,
} from "@/api/pickup-bookings";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { useListPickupSlots } from "@/api/pickup-slots";
import { formatCents } from "@/api/carrier-rate-bands";
import axios from "axios";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

/**
 * Admin pickup-bookings dispatch view.
 *
 * Bookings are created at order-create time by createUnifiedOrder's
 * find-or-attach logic — this page inspects them and supports Phase 11a
 * actions (cancel + per-shipment detach + notes edit + print). Reschedule /
 * refund / capacity stay deferred to Phase 11c.
 *
 * State column is task-derived, not booking.status — see Phase 10 decisions.
 * `booking.status` is now just an admin kill-switch (CANCELLED) but the
 * cancel path here also cascades pickup-task cancellation via the shared
 * server service.
 */

const ALL = "__all__";

const STATE_VARIANT: Record<
  PickupBookingDerivedState,
  "default" | "secondary" | "destructive" | "outline"
> = {
  Scheduled: "outline",
  InProgress: "default",
  Completed: "secondary",
  Failed: "destructive",
  Cancelled: "destructive",
};

const STATE_LABEL: Record<PickupBookingDerivedState, string> = {
  Scheduled: "Scheduled",
  InProgress: "In progress",
  Completed: "Completed",
  Failed: "Failed",
  Cancelled: "Cancelled",
};

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function summarizeCustomers(customers: { id: string; name: string }[]): string {
  if (customers.length === 0) return "—";
  if (customers.length === 1) return customers[0].name;
  return `${customers[0].name} +${customers.length - 1}`;
}

function notesPreview(notes: string | null | undefined, max = 30): string {
  if (!notes) return "";
  const trimmed = notes.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

function shipmentTaskProgress(
  shipments: AdminPickupBooking["shipments"]
): string {
  const active = shipments.filter((s) => s.status !== "CANCELLED");
  const tasks = active.flatMap((s) => s.tasks);
  if (tasks.length === 0) return "0/0 tasks";
  const completed = tasks.filter((t) => t.status === "COMPLETED").length;
  return `${completed}/${tasks.length} tasks`;
}

export function PickupBookingsScreen() {
  const [stateFilter, setStateFilter] = useState<string>(ALL);
  const [dispatchFilter, setDispatchFilter] = useState<string>(ALL);
  const [fromDate, setFromDate] = useState<string>(todayIso());
  const [toDate, setToDate] = useState<string>(""); // empty = no upper bound
  const [detailId, setDetailId] = useState<string | null>(null);

  const filterParams = useMemo(
    () => ({
      state:
        stateFilter === ALL
          ? undefined
          : (stateFilter as PickupBookingDerivedState),
      dispatchCenterId: dispatchFilter === ALL ? undefined : dispatchFilter,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [stateFilter, dispatchFilter, fromDate, toDate]
  );

  const {
    data: allBookings,
    isLoading,
    refetch,
  } = useGetPickupBookings(filterParams);
  const { data: dispatchCenters } = useGetDispatchCenters();
  // Carrier scope narrows the list to bookings whose dispatch is operated by
  // one of the selected carriers; dispatch-center scope narrows further to
  // bookings on the selected centers. Both filters are client-side today.
  // Empty carrier selection = nothing (match the global semantic).
  const {
    carrierIds: scopedCarrierIds,
    dispatchCenterIds: scopedDispatchCenterIds,
  } = useCarrierScope();
  const bookings = useMemo(() => {
    const all = allBookings ?? [];
    if (scopedCarrierIds.length === 0) return [];
    const carrierSet = new Set(scopedCarrierIds);
    let out = all.filter((b) => {
      // A booking whose operator carrier is unknown can't belong to any
      // selected carrier scope, so exclude it rather than crash.
      const operatorId = b.dispatchCenter.operatorCarrier?.id;
      return operatorId != null && carrierSet.has(operatorId);
    });
    if (scopedDispatchCenterIds.length > 0) {
      const centerSet = new Set(scopedDispatchCenterIds);
      out = out.filter((b) => centerSet.has(b.dispatchCenter.id));
    }
    return out;
  }, [allBookings, scopedCarrierIds, scopedDispatchCenterIds]);

  const detailBooking = bookings?.find((b) => b.id === detailId) ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Pickup Bookings
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          className="h-8"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Scheduled pickup runs. Bookings are created automatically when customers
        attach a pickup at order time. State is derived from driver task
        progress. Admin can cancel, detach, reschedule, edit notes, and print
        from the detail view.
      </p>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">State</Label>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All states</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="InProgress">In progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Dispatch center</Label>
            <Select value={dispatchFilter} onValueChange={setDispatchFilter}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All dispatches</SelectItem>
                {(dispatchCenters ?? []).map((dc) => (
                  <SelectItem key={dc.id} value={dc.id}>
                    {dc.name} ({dc.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From date</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To date (optional)</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            {bookings?.length ?? 0} booking
            {bookings?.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date / slot</TableHead>
                <TableHead>Pickup address</TableHead>
                <TableHead>Customer(s)</TableHead>
                <TableHead>Dispatch</TableHead>
                <TableHead>Shipments</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(bookings ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    No pickup bookings match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                (bookings ?? []).map((b) => {
                  const revenue = b.shipments.reduce(
                    (sum, s) => sum + (s.pickupPriceCents ?? 0),
                    0
                  );
                  const stateLabel = STATE_LABEL[b.derivedState];
                  const cancelledCount =
                    b.totalShipmentCount - b.attachedShipmentCount;
                  return (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div className="text-sm font-medium flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(b.scheduledDate)}
                        </div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {b.slot.name} ({b.slot.startTime}–{b.slot.endTime})
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {b.pickupAddress.line1}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {b.pickupAddress.city}, {b.pickupAddress.province}{" "}
                          {b.pickupAddress.postalCode}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {summarizeCustomers(b.customers)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {b.dispatchCenter.code}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {b.attachedShipmentCount}
                          {cancelledCount > 0 && (
                            <span className="text-[10px] text-muted-foreground ml-1">
                              ({cancelledCount} cancelled)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {formatCents(revenue)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <Badge
                            variant={STATE_VARIANT[b.derivedState]}
                            className="text-[10px] px-1.5 py-0 w-fit"
                          >
                            {stateLabel}
                          </Badge>
                          {b.derivedState !== "Cancelled" && (
                            <span className="text-[10px] text-muted-foreground">
                              {shipmentTaskProgress(b.shipments)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">
                        {notesPreview(b.notes)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => setDetailId(b.id)}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      {detailBooking && (
        <BookingDetailDialog
          booking={detailBooking}
          open={!!detailId}
          onOpenChange={(o) => !o && setDetailId(null)}
        />
      )}
    </div>
  );
}

interface BookingDetailDialogProps {
  booking: AdminPickupBooking;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

function BookingDetailDialog({
  booking,
  open,
  onOpenChange,
}: BookingDetailDialogProps) {
  const totalPickupRevenue = booking.shipments.reduce(
    (sum, s) => sum + (s.pickupPriceCents ?? 0),
    0
  );

  const cancelMutation = useCancelPickupBookingAdmin();
  const detachMutation = useDetachShipmentFromPickupBookingAdmin();
  const updateNotesMutation = useUpdatePickupBookingNotesAdmin();
  const printMutation = usePrintPickupBookingAdmin();
  const rescheduleMutation = useReschedulePickupBookingAdmin();
  // Reschedule slot picker — slots are now per-(service, dispatch). Use the
  // booking's service + dispatch to query candidates. For now the reschedule
  // flow keeps the same slot id; cross-slot reschedules are a follow-up.
  const { data: candidateSlots } = useListPickupSlots(
    booking.pickupService.id,
    { dispatchCenterId: booking.dispatchCenter.id }
  );

  const isReadOnly = booking.derivedState !== "Scheduled";

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<string>(
    booking.scheduledDate.slice(0, 10)
  );
  const [rescheduleSlotId, setRescheduleSlotId] = useState<string>(
    booking.slot.id
  );
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  useEffect(() => {
    setRescheduleDate(booking.scheduledDate.slice(0, 10));
    setRescheduleSlotId(booking.slot.id);
    setRescheduleError(null);
  }, [booking.id, booking.scheduledDate, booking.slot.id]);

  const pickupSlots = (candidateSlots ?? []).filter((s) => s.isActive);

  const [notesDraft, setNotesDraft] = useState(booking.notes ?? "");
  useEffect(() => {
    setNotesDraft(booking.notes ?? "");
  }, [booking.id, booking.notes]);

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [pendingDetachShipmentId, setPendingDetachShipmentId] = useState<
    string | null
  >(null);
  const [blockedAlertOpen, setBlockedAlertOpen] = useState(false);

  const handleSaveNotes = async () => {
    const next = notesDraft.trim() === "" ? null : notesDraft;
    if (next === (booking.notes ?? null)) return;
    try {
      await updateNotesMutation.mutateAsync({
        bookingId: booking.id,
        notes: next,
      });
      toast.success("Notes saved");
    } catch {
      toast.error("Couldn't save notes");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({ bookingId: booking.id });
      setConfirmCancelOpen(false);
      onOpenChange(false);
      toast.success("Pickup cancelled", {
        description:
          "Pending pickup tasks were cancelled. Customer not yet notified (notification system pending).",
      });
    } catch (err) {
      setConfirmCancelOpen(false);
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setBlockedAlertOpen(true);
        return;
      }
      toast.error("Couldn't cancel");
    }
  };

  const handleReschedule = async () => {
    setRescheduleError(null);
    try {
      const result = await rescheduleMutation.mutateAsync({
        bookingId: booking.id,
        newScheduledDate: rescheduleDate,
        newSlotId: rescheduleSlotId,
        shipmentIds: booking.shipments
          .filter((shipment) => shipment.status !== "CANCELLED")
          .map((shipment) => shipment.id),
      });
      setRescheduleOpen(false);
      toast.success("Pickup rescheduled", {
        description: `Moved to ${formatDate(result.booking.scheduledDate)}`,
      });
    } catch (err) {
      if (isAdminRescheduleStructuredError(err)) {
        const code = err.response?.data.error;
        if (code === "PRICE_CHANGED") {
          setRescheduleError(
            "Pricing for the new date/slot differs from the current booking. Cancel + recreate to change tiers."
          );
          return;
        }
        if (code === "CONFLICTING_BOOKING") {
          setRescheduleError(
            "This customer already has a pickup at the selected date + slot."
          );
          return;
        }
        if (code === "PAST_DATE") {
          setRescheduleError("New date can't be in the past.");
          return;
        }
        if (code === "BOOKING_NOT_ATTACHABLE") {
          setRescheduleError(
            "Booking can't be rescheduled — it's cancelled, in progress, or completed."
          );
          return;
        }
        setRescheduleError(
          err.response?.data.message ?? "Couldn't reschedule."
        );
        return;
      }
      setRescheduleError("Couldn't reschedule. Try again.");
    }
  };

  const handleDetach = async () => {
    if (!pendingDetachShipmentId) return;
    try {
      const result = await detachMutation.mutateAsync({
        bookingId: booking.id,
        shipmentId: pendingDetachShipmentId,
      });
      setPendingDetachShipmentId(null);
      if (result.booking.cancelledAt) {
        toast.success("Pickup auto-cancelled", {
          description: "Last shipment detached, booking cancelled.",
        });
        onOpenChange(false);
      } else {
        toast.success("Shipment detached");
      }
    } catch (err) {
      setPendingDetachShipmentId(null);
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setBlockedAlertOpen(true);
        return;
      }
      toast.error("Couldn't detach shipment");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Pickup booking
            <Badge
              variant={STATE_VARIANT[booking.derivedState]}
              className="text-[10px] px-1.5 py-0"
            >
              {STATE_LABEL[booking.derivedState]}
            </Badge>
            {!booking.cancelledAt && booking.derivedState !== "Cancelled" && (
              <span className="text-[10px] text-muted-foreground">
                {shipmentTaskProgress(booking.shipments)}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => printMutation.mutate(booking.id)}
                disabled={printMutation.isPending}
                className="h-7 px-2 text-xs"
              >
                {printMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Printer className="h-3 w-3 mr-1" />
                )}
                Print
              </Button>
              {!isReadOnly && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRescheduleOpen(true)}
                    className="h-7 px-2 text-xs"
                  >
                    <CalendarClock className="h-3 w-3 mr-1" />
                    Reschedule
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmCancelOpen(true)}
                    disabled={cancelMutation.isPending}
                    className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  >
                    Cancel booking
                  </Button>
                </>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header summary */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-xs text-muted-foreground">
                Date / slot
              </Label>
              <div className="font-medium">
                {formatDate(booking.scheduledDate)}
              </div>
              <div className="text-xs text-muted-foreground">
                {booking.slot.name} ({booking.slot.startTime}–
                {booking.slot.endTime})
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Dispatch</Label>
              <div className="font-medium">{booking.dispatchCenter.name}</div>
              {booking.dispatchCenter.operatorCarrier && (
                <div className="text-xs text-muted-foreground">
                  Operated by {booking.dispatchCenter.operatorCarrier.name}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">
                Pickup address
              </Label>
              <div className="font-medium">{booking.pickupAddress.line1}</div>
              <div className="text-xs text-muted-foreground">
                {booking.pickupAddress.city}, {booking.pickupAddress.province}{" "}
                {booking.pickupAddress.postalCode}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">
                Customer(s)
              </Label>
              <div className="font-medium">
                {booking.customers.length === 0
                  ? "—"
                  : booking.customers.map((c) => c.name).join(", ")}
              </div>
            </div>
          </div>

          {/* Notes (editable when scheduled) */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value.slice(0, 500))}
              onBlur={handleSaveNotes}
              rows={2}
              disabled={isReadOnly || updateNotesMutation.isPending}
              placeholder="Driver notes, gate code, parking instructions…"
            />
            <p className="text-[10px] text-muted-foreground text-right">
              {notesDraft.length}/500
            </p>
          </div>

          {/* Attached shipments + per-shipment task status */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Attached shipments ({booking.attachedShipmentCount} active
              {booking.totalShipmentCount > booking.attachedShipmentCount
                ? `, ${booking.totalShipmentCount - booking.attachedShipmentCount} cancelled`
                : ""}
              ) — total {formatCents(totalPickupRevenue)}
            </Label>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="h-8 text-xs px-2">#</TableHead>
                    <TableHead className="h-8 text-xs px-2">Tracking</TableHead>
                    <TableHead className="h-8 text-xs px-2">Carrier</TableHead>
                    <TableHead className="h-8 text-xs px-2">
                      Shipment status
                    </TableHead>
                    <TableHead className="h-8 text-xs px-2">
                      Pickup task
                    </TableHead>
                    <TableHead className="h-8 text-xs px-2">Pickup $</TableHead>
                    <TableHead className="h-8 text-xs px-2 w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {booking.shipments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-3 text-xs text-muted-foreground"
                      >
                        No shipments attached.
                      </TableCell>
                    </TableRow>
                  ) : (
                    booking.shipments.map((s) => {
                      const taskStatus =
                        s.tasks.length === 0
                          ? "—"
                          : s.tasks.map((t) => t.status).join(", ");
                      const canDetach =
                        !isReadOnly &&
                        s.tasks.every(
                          (t) =>
                            t.status === "PENDING" || t.status === "CANCELLED"
                        );
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="py-1 px-2 text-xs">
                            {s.shipmentNumber}
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs font-mono">
                            {s.trackingNumber ?? "—"}
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs">
                            {s.carrier?.name ?? "—"}
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs">
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0"
                            >
                              {s.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs">
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0"
                            >
                              {taskStatus}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs">
                            {s.pickupPriceCents != null
                              ? formatCents(s.pickupPriceCents)
                              : "—"}
                          </TableCell>
                          <TableCell className="py-1 px-2 text-xs">
                            {canDetach && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-1.5 text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => setPendingDetachShipmentId(s.id)}
                                disabled={detachMutation.isPending}
                              >
                                <X className="h-3 w-3 mr-0.5" />
                                Detach
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Phase 12 (#232): bundle-discount + tax breakdown when applicable */}
            {(booking.bundleDiscountCents > 0 || booking.taxCents > 0) && (
              <div className="text-xs space-y-0.5 pl-1">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>{formatCents(totalPickupRevenue)}</span>
                </div>
                {booking.bundleDiscountCents > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="inline-flex items-center gap-1">
                      Bundle discount ({booking.attachedShipmentCount}{" "}
                      shipments)
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-emerald-700/60 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            Bundle discount tiers apply at attach time (1st
                            shipment full price, 2nd 50% off, 3rd+ 75% off) and
                            stay locked even if other shipments detach. This
                            keeps refund math deterministic — each shipment's
                            price doesn't change based on its siblings'
                            lifecycle.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                    <span>−{formatCents(booking.bundleDiscountCents)}</span>
                  </div>
                )}
                {booking.taxCents > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax</span>
                    <span>{formatCents(booking.taxCents)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-0.5 border-t border-stone-200">
                  <span>Total charged</span>
                  <span>
                    {formatCents(
                      totalPickupRevenue -
                        booking.bundleDiscountCents +
                        booking.taxCents
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground italic">
            Reschedule moves the booking to a new date/slot at the same price.
            Tier changes (different region/service) require cancel + recreate.
            Refund requests appear in the Refund Requests queue.
          </div>
        </div>
      </DialogContent>

      {/* Cancel confirmation */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this pickup booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {booking.attachedShipmentCount} shipment
              {booking.attachedShipmentCount === 1 ? "" : "s"} will detach.
              Pending pickup tasks will be cancelled. The customer is not
              auto-notified yet — refund (if any) processed manually.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              )}
              Cancel booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detach confirmation */}
      <AlertDialog
        open={pendingDetachShipmentId !== null}
        onOpenChange={(o) => !o && setPendingDetachShipmentId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Detach this shipment from the booking?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  The shipment stays active. Its pickup task is cancelled. If
                  this is the last shipment, the booking auto-cancels.
                </p>
                {booking.bundleDiscountCents > 0 && (
                  <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                    Sibling shipments keep the bundle discount allocated at
                    their attach time — this is intentional (sticky-by-design)
                    so refund math stays deterministic. Only the detached
                    shipment's pickup fee enters the refund flow.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDetach}
              className="bg-red-600 hover:bg-red-700"
              disabled={detachMutation.isPending}
            >
              {detachMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              )}
              Detach
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              Reschedule pickup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Currently {formatDate(booking.scheduledDate)}, {booking.slot.name}{" "}
              ({booking.slot.startTime}–{booking.slot.endTime}).
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New date</Label>
              <Input
                type="date"
                value={rescheduleDate}
                min={todayIso()}
                onChange={(e) => {
                  setRescheduleDate(e.target.value);
                  setRescheduleError(null);
                }}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New slot</Label>
              <Select
                value={rescheduleSlotId}
                onValueChange={(v) => {
                  setRescheduleSlotId(v);
                  setRescheduleError(null);
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pickupSlots.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.startTime}–{s.endTime})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rescheduleError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                {rescheduleError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRescheduleOpen(false)}
                disabled={rescheduleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => void handleReschedule()}
                disabled={
                  rescheduleMutation.isPending ||
                  (rescheduleDate === booking.scheduledDate.slice(0, 10) &&
                    rescheduleSlotId === booking.slot.id)
                }
              >
                {rescheduleMutation.isPending && (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                )}
                Reschedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blocked-by-in-progress alert */}
      <AlertDialog open={blockedAlertOpen} onOpenChange={setBlockedAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Action blocked</AlertDialogTitle>
            <AlertDialogDescription>
              The driver run for this booking has already started. Cancel /
              detach via the in-progress task workflows on the operator side
              instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setBlockedAlertOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
